import React, { useMemo } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import type { TextContent } from '@flows/core/i18n/types'
import type { SearchableList as SearchableListProps } from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import { resolveElementOptions } from '@flows/renderer/utils/options'
import UiKitSearchableList from '@ui-kit/components/formComponents/SearchableList'
import type { SearchableListOption } from '@ui-kit/components/formComponents/SearchableList/types'

type Props = SearchableListProps<any, any> & {
  flowValues: FlowValues
  locale?: string
}

const processOptionalText = (
  textProcessor: ReturnType<typeof processStepContentElementText>,
  text?: TextContent
) => (text ? textProcessor(text) : undefined)

export const SearchableList = (props: Props) => {
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const {
    options: configOptions,
    optionsTransformer,
    filterOptions,
    exclusiveValues,
    flowValues,
  } = props
  const textProcessor = processStepContentElementText(t, { flowValues, servicesResponses })

  const isMultiple = props.selectionMode === 'multiple'

  const { control } = useFormContext()
  const { field, fieldState } = useController({
    control,
    name: props.name,
    ...(isMultiple ? { defaultValue: [] } : {}),
  })

  const options = useMemo<SearchableListOption[]>(() => {
    const translateLabel = processStepContentElementText(t, { flowValues, servicesResponses })
    const resolvedOptions = resolveElementOptions(
      { options: configOptions, optionsTransformer, filterOptions, exclusiveValues },
      flowValues
    )
    return resolvedOptions.map((option) => ({
      value: option.value,
      label: typeof option.label === 'string' ? option.label : translateLabel(option.label),
      image: option.image,
      icon: option.icon,
    }))
  }, [
    configOptions,
    optionsTransformer,
    filterOptions,
    exclusiveValues,
    flowValues,
    t,
    servicesResponses,
  ])

  const commonProps = {
    name: props.name,
    testID: props.name,
    title: processOptionalText(textProcessor, props.title),
    searchPlaceholder: processOptionalText(textProcessor, props.searchPlaceholder),
    options,
    popularOptions: props.popularOptions,
    pinnedOptions: props.pinnedOptions,
    groupBy: props.groupBy,
    locale: props.locale,
    disabled: props.disabled,
    errorMessage: fieldState.error?.message,
    selectedIcon: props.selectedIcon,
    labels: {
      popular: t('sharedFlows_common:searchableList.popular'),
      all: t('sharedFlows_common:searchableList.all'),
      noResults: t('sharedFlows_common:searchableList.noResults'),
      removeSelection: t('sharedFlows_common:searchableList.removeSelection'),
    },
  }

  if (isMultiple) {
    return (
      <UiKitSearchableList
        {...commonProps}
        selectionMode="multiple"
        value={field.value}
        maxSelections={props.maxSelections}
        exclusiveValues={exclusiveValues}
        onValueChange={(value) => field.onChange(value)}
      />
    )
  }

  return (
    <UiKitSearchableList
      {...commonProps}
      value={field.value}
      onValueChange={(value) => field.onChange(value)}
    />
  )
}
